import { useState, useEffect, createContext, useContext } from "react";
import PropTypes from "prop-types";
import { translations, defaultLanguage, supportedLanguages } from "./translations";

// Create context for language
const LanguageContext = createContext();

// Language detection from user agent
function detectLanguage(userAgent) {
  if (!userAgent) return defaultLanguage;

  const lang = userAgent.toLowerCase();

  // Check for English-speaking locales
  if (lang.includes("en-") || lang.includes("en.")) {
    return "en";
  }

  // Check for Dutch locale
  if (lang.includes("nl-") || lang.includes("nl.")) {
    return "nl";
  }

  // Check for common language indicators in user agent
  if (lang.includes("chrome") && !lang.includes("nl") && !lang.includes("dutch")) {
    // Chrome on non-Dutch systems - likely English
    // But let's default to Dutch as fallback unless explicitly English
  }

  // Check for explicit English preferences
  const englishIndicators = ["firefox", "safari", "apple"];
  for (const indicator of englishIndicators) {
    if (lang.includes(indicator) && !lang.includes("nl") && !lang.includes("dutch")) {
      // Could be English but let's still prefer Dutch as default per requirements
    }
  }

  // Default to Dutch
  return defaultLanguage;
}

// Get initial language
function getInitialLanguage() {
  // Check localStorage first (for persisted preference)
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem("emtay-language");
    if (stored && (stored === "nl" || stored === "en")) {
      return stored;
    }

    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get("lang");
    if (urlLang && (urlLang === "nl" || urlLang === "en")) {
      return urlLang;
    }

    // Detect from user agent
    const userAgent = navigator.userAgent;
    return detectLanguage(userAgent);
  }

  return defaultLanguage;
}

// Language provider component
export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage);

  // Persist language choice
  useEffect(() => {
    localStorage.setItem("emtay-language", language);
  }, [language]);

  // Get translation for a key path (e.g., "hero.title")
  const t = (keyPath) => {
    const keys = keyPath.split(".");
    let value = translations[language];

    for (const key of keys) {
      if (value && typeof value === "object" && key in value) {
        value = value[key];
      } else {
        // Fallback to default language
        value = translations[defaultLanguage];
        for (const k of keys) {
          if (value && typeof value === "object" && k in value) {
            value = value[k];
          } else {
            return keyPath; // Return key if not found
          }
        }
        break;
      }
    }

    return value || keyPath;
  };

  // Change language
  const changeLanguage = (lang) => {
    if (lang === "nl" || lang === "en") {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage: changeLanguage, t, supportedLanguages }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

// Setting default values for the props of LanguageProvider
LanguageProvider.defaultProps = {
  children: null,
};

// Typechecking props for the LanguageProvider
LanguageProvider.propTypes = {
  children: PropTypes.node,
};

// Hook to use language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export default LanguageProvider;
